import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { DoctorList, PaginatedDoctorList } from '@/types/settings-api';

/**
 * Org practitioner (doctor) list.
 *
 * `GET /api/v1/Practice/{organizationId}/PractitionerRoles?role=doctor&showArchived={bool}`
 *
 * Ported from dev-scrips-pm-react `src/settings/services/use-doctors-list.ts`
 * (PROD-760) for PROD-1724 — backs the consent provider-signature picker, where
 * a non-doctor filler selects whose stored signature to apply.
 *
 * Two non-obvious wire details carried over from the PM port:
 *  1. The path is `/api/v1/Practice/{orgId}/PractitionerRoles`, NOT
 *     `/api/Doctor/PractitionerRoles` (that REST guess 404s on dev).
 *  2. The response is a paginated wrapper — rows live under `.results`, not a
 *     bare array. Unwrapped here so consumers stay simple.
 */
export function useDoctorsList(
  organizationId: string | null | undefined,
  showArchived: boolean,
) {
  return useQuery({
    queryKey: ['settings', 'doctors-list', organizationId, showArchived],
    queryFn: async ({ signal }) => {
      if (!organizationId) throw new Error('useDoctorsList called without orgId');
      const res = await get<PaginatedDoctorList>(
        `/api/v1/Practice/${organizationId}/PractitionerRoles?role=doctor&showArchived=${showArchived}`,
        { signal },
      );
      return (res.results ?? []) as DoctorList[];
    },
    enabled: !!organizationId,
  });
}
